/* eslint-disable arrow-body-style */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { Timeline, Bubble } from 'tea-component';
import EllipsisHover from '@gp/components/EllipsisHover';
import { getTextWidth, calcCurrentNextIndex } from './utils';
import type { IBaseProps } from '@home/types/common';
import type { ITimeline } from './types';
import '@gp/pages/Pipeline/ProjectDetails/components/MilestonesTimelines/index.scss';
import { MILESTONE_CATEGORY_MAP } from '@gp/constants/const';

interface IMilestonesTimelinesProps extends IBaseProps {
  timelines: any[];
  isSame: boolean;
  current: number;
  stage?: string;
  getTimelineOffsetMsg: (v: any) => void;
}

const MilestonesTimelines: React.FC<IMilestonesTimelinesProps> = (props) => {
  const {
    timelines,
    timelinesCur,
    isSame,
    current: currentProps,
    stage,
    getTimelineOffsetMsg,
    milestoneCategory,
    setTimelinesWidth,
    milestoneLegendList,
  } = props;

  const stageUpperCase = stage?.toUpperCase() || '';
  const { current, next } = calcCurrentNextIndex(timelines, currentProps);
  const timelinesLength = timelines?.length || 0;
  const milestonesTimelinesRef = useRef(null);
  const [firstItemOverflow, setFirstItemOverflow] = useState(false);
  const [lastItemOverflow, setLastItemOverflow] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pl = useMemo<number>(() => {
    const firstCon = timelines?.[0]?.content;
    const lastCon = timelines?.[timelines?.length - 1]?.content;
    let pl = 20;
    const leftOffsetMsg = {
      offset: 20,
      isOverflow: false,
    };
    const rightOffsetMsg = {
      offset: 0,
      isOverflow: false,
    };
    // 左侧偏移量(paddingLeft)及第一个文案是否溢出
    if (firstCon) {
      const { width, isOverflow } = getTextWidth(firstCon, 230);
      pl = width / 2 > 20 ? width / 2 : 20;
      leftOffsetMsg.offset = pl;
      leftOffsetMsg.isOverflow = isOverflow;
      setFirstItemOverflow(isOverflow);
    }
    // 右侧偏移量(paddingLeft)及最后一个文案是否溢出
    if (timelines?.length > 1) {
      rightOffsetMsg.offset = 20;
      if (lastCon) {
        const { width, isOverflow } = getTextWidth(lastCon, 230);
        const rightOffset = width / 2 > 20 ? width / 2 : 20;
        rightOffsetMsg.offset = rightOffset;
        rightOffsetMsg.isOverflow = isOverflow;
        setLastItemOverflow(isOverflow);
      }
    }
    getTimelineOffsetMsg?.({
      left: leftOffsetMsg,
      right: rightOffsetMsg,
    });

    return pl;
  }, [timelines]);

  const lastItemshadow: any = useMemo<number>(() => {
    if (Array.isArray(timelines) && timelines?.length > 0) {
      return timelines[timelines.length - 1];
    }
    return {} as any;
  }, [timelines]);

  // 添加失效项目状态
  const noPerProject = useMemo(() => {
    return stageUpperCase === 'CANCELED' || stageUpperCase === 'NO MAINTENANCE';
  }, [stageUpperCase]);

  useEffect(() => {
    if (milestonesTimelinesRef.current) {
      // 获取元素完整外尺寸（包含padding和border）
      const elementWidth = milestonesTimelinesRef.current.offsetWidth;
      setTimelinesWidth?.(elementWidth);
    }
  }, [timelines]);

  const getTypeClassName = (timeline: any, index: any) => {
    const { category } = timeline || {};
    const isNextAction = !isSame && index === next;

    if (category === 'check_in') {
      if (stageUpperCase === 'LIVE') {
        if (timeline?.milestone_id === lastItemshadow?.milestone_id) {
          return '--react-rectangle';
        }
        return '--rectangle';
      }
      if (
        index === current &&
        stageUpperCase !== 'CANCELED' &&
        stageUpperCase !== 'NO MAINTENANCE'
      ) {
        return '--react-rectangle';
      }
      if ((index > current && !isNextAction) || (isNextAction && noPerProject)) {
        return '--rectangle --gray';
      }
      if (index < current || isNextAction || (index === current && noPerProject)) {
        return '--rectangle';
      }
    } else if (category === 'gr') {
      if (stageUpperCase === 'LIVE') {
        if (timeline?.milestone_id === lastItemshadow?.milestone_id) {
          return '--flag-shadow';
        }
        return '--flag';
      }
      if (
        index === current &&
        stageUpperCase !== 'CANCELED' &&
        stageUpperCase !== 'NO MAINTENANCE'
      ) {
        return '--flag-shadow';
      }
      if ((index > current && !isNextAction) || (isNextAction && noPerProject)) {
        return '--flag --gray';
      }
      if (index < current || isNextAction || (index === current && noPerProject)) {
        return '--flag';
      }
    } else {
      if (stageUpperCase === 'LIVE') {
        if (timeline?.milestone_id === lastItemshadow?.milestone_id) {
          return '--circle-shadow';
        }
      } else {
        if (
          index === current &&
          stageUpperCase !== 'CANCELED' &&
          stageUpperCase !== 'NO MAINTENANCE'
        ) {
          return '--circle-shadow';
        }
      }
    }
  };

  return (
    <div
      // style={{ paddingLeft: pl }}
      className="dbg-milestones-timeline-container"
    >
      {/* <Timeline
        mode="horizontal"
        className={classnames('milestones-timeline', {
          'is-live': stageUpperCase === 'LIVE',
          'is-canceled': stageUpperCase === 'CANCELED',
          'is-no-maintenance': stageUpperCase === 'NO MAINTENANCE',
        })}
      >
        {timelines?.map((timeline, index) => {
          const { id, content, label } = timeline || {};
          return (
            <Timeline.Item
              key={`${index}_${id}_${content}`}
              className={classnames('milestones-timeline-item', {
                'action-style': index < current,
                'now-action': index === current,
                'now-next-action': !isSame && index > current && index < next,
                'next-action': !isSame && index === next,
                'is-same': isSame && index === current,
                'is-first-item': index === 0,
                'is-last-item': index === timelinesLength - 1,
                'first-item-con-align-l': index === 0 && firstItemOverflow,
                'last-item-con-align-l': index === timelinesLength - 1 && lastItemOverflow,
              })}
              label={
                <div>
                  <EllipsisHover className="milestones-timeline-name" text={content} />
                  <div className="milestones-timeline-time">{label || '-'}</div>
                </div>
              }
            />
          );
        })}
      </Timeline> */}

      <Timeline
        mode="horizontal"
        className={classnames('milestones-timeline', {
          'is-live': stageUpperCase === 'LIVE',
          'is-canceled': stageUpperCase === 'CANCELED',
          'is-no-maintenance': stageUpperCase === 'NO MAINTENANCE',
        })}
        ref={milestonesTimelinesRef}
      >
        {/* --rectangle: 矩形：  --flag: 旗帜icon   --circle-shadow:圆形扩散动效  --react-rectangle: 矩形扩散动效  --gray: 灰色*/}
        {timelines?.map((timeline, index) => {
          const { id, content, label, category } = timeline || {};
          // const timelinesCurLength = timelinesCur?.length;
          const timelinesCurEnd = timelinesCur?.[timelinesCur.length - 1];

          if (milestoneLegendList?.includes(category)) {
            return (
              <Timeline.Item
                key={`${index}_${id}_${content}`}
                className={classnames(
                  'milestones-timeline-item',
                  {
                    'action-style': index < current,
                    'now-action':
                      index === current &&
                      stageUpperCase !== 'LIVE' &&
                      stageUpperCase !== 'CANCELED' &&
                      stageUpperCase !== 'NO MAINTENANCE',
                    'now-next-action': !isSame && index > current && index < next,
                    'next-action':
                      !isSame &&
                      index === next &&
                      stageUpperCase !== 'LIVE' &&
                      stageUpperCase !== 'CANCELED' &&
                      stageUpperCase !== 'NO MAINTENANCE',
                    'is-first-item': index === 0,
                    'is-last-item':
                      index === timelinesLength - 1 ||
                      timeline?.milestone_id === timelinesCurEnd?.milestone_id,
                    'first-item-con-align-l': index === 0 && firstItemOverflow,
                    'last-item-con-align-l': index === timelinesLength - 1 && lastItemOverflow,
                    'is-last-item-shadow':
                      timeline?.milestone_id === lastItemshadow?.milestone_id &&
                      stageUpperCase === 'LIVE',
                    'solid-dot':
                      (category !== 'check_in' && category !== 'gr' && stageUpperCase === 'LIVE') ||
                      (index < current && stageUpperCase !== 'LIVE'),
                  },
                  getTypeClassName(timeline, index),
                )}
                label={
                  <Bubble
                    dark
                    content={MILESTONE_CATEGORY_MAP?.[category]}
                    placement={'top'}
                    // placement={'bottom'}
                    // placementOffset={35}
                  >
                    <div className="dbg-milestones-timeline-div">
                      <EllipsisHover className="milestones-timeline-name" text={content} />
                      <div className="milestones-timeline-time">{label || '-'}</div>
                    </div>
                  </Bubble>
                }
              />
            );
          }
          return null;
        })}
        {/* <Timeline.Item
          className="milestones-timeline-item action-style --rectangle"
          label={
            <div>
              <EllipsisHover className="milestones-timeline-name" text="End of Pre" />
              <div className="milestones-timeline-time">2025-09</div>
            </div>
          }
        />
        <Timeline.Item
          className="milestones-timeline-item action-style"
          label={
            <Bubble dark content="Game overview edit" placement={'bottom'} placementOffset={35}>
              <div>
                <EllipsisHover className="milestones-timeline-name" text="End of Pre" />
                <div className="milestones-timeline-time">2025-09</div>
              </div>
            </Bubble>
          }
        />
        <Timeline.Item
          className="milestones-timeline-item action-style --flag"
          label={
            <div>
              <EllipsisHover className="milestones-timeline-name" text="End of Pre" />
              <div className="milestones-timeline-time">2025-09</div>
            </div>
          }
        />
        <Timeline.Item
          className="milestones-timeline-item now-action --circle-shadow"
          label={
            <div>
              <EllipsisHover className="milestones-timeline-name" text="End of Pre-Production" />
              <div className="milestones-timeline-time">2025-09</div>
            </div>
          }
        />

        <Timeline.Item
          className="milestones-timeline-item now-action --react-rectangle"
          label={
            <Bubble dark content="Game overview edit" placement={'bottom'} placementOffset={45}>
              <div>
                <EllipsisHover className="milestones-timeline-name" text="End of Pre-Production" />
                <div className="milestones-timeline-time">2025-09</div>
              </div>
            </Bubble>
          }
        />

        <Timeline.Item
          className="milestones-timeline-item  --flag --gray"
          label={
            <div>
              <EllipsisHover className="milestones-timeline-name" text="End of Pre-Production" />
              <div className="milestones-timeline-time">2025-09</div>
            </div>
          }
        />

        <Timeline.Item
          className="milestones-timeline-item"
          label={
            <div>
              <EllipsisHover className="milestones-timeline-name" text="End of Pre-Production" />
              <div className="milestones-timeline-time">2025-09</div>
            </div>
          }
        /> */}
      </Timeline>
    </div>
  );
};

export default React.memo(MilestonesTimelines);
